import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { authClient, useSession } from '../auth-client.ts';
import { api } from '../api.ts';
import { PositionLockSetting } from '../device.tsx';
import { deleteAccountDb } from '../local/db.ts';
import { markActive, signOutAndLeave, useSite } from '../idle.tsx';
import { Link, navigate, safeNext, useLocation } from '../router.tsx';
import { ago, deviceName, t } from '../strings.ts';
import { ThemeSwitch } from '../theme.tsx';
import { SiteCode } from '../site-code.tsx';
import { TipButton } from '../tip.tsx';
import { DevMailboxHint } from './mailbox.tsx';
import { ContactButton } from '../contact.tsx';

/** "Locked out or stuck? Contact us": the Contact us window, its subject filled in (2026-11-01). */
function HelpLine() {
  return <p className="muted small link-row">{t('troubleSigningIn')} <ContactButton subject={t('contactSubjectSignIn')} /></p>;
}

const VERIFIED_CALLBACK = '/?verified=1';

export function AuthCard({ title, intro, brand, children }: { title: string; intro?: string; brand?: string; children: ReactNode }) {
  return (
    <main className="auth">
      <div className="auth-card">
        <div className="brand">{brand ?? t('appName')}</div>
        <h1>{title}</h1>
        {intro && <p className="muted">{intro}</p>}
        {children}
      </div>
    </main>
  );
}

/**
 * The address typed on the sign-in page, carried to "Forgot your password?" so it need not be
 * typed again - in this tab's sessionStorage, never in the address bar.
 */
const EMAIL_KEY = 'fmc.email';
const rememberEmail = (email: string) => {
  try { sessionStorage.setItem(EMAIL_KEY, email); } catch { /* private window: typed again */ }
};
export const recallEmail = () => {
  try { return sessionStorage.getItem(EMAIL_KEY) || ''; } catch { return ''; }
};

/** A password box with "Forgot your password?" on its label's line, where people look for it. */
export function PasswordField({ id, label, value, onChange, autoComplete, forgot, minLength, hint }: {
  id: string; label: string; value: string; onChange: (v: string) => void; autoComplete: string;
  forgot?: ReactNode; minLength?: number; hint?: string;
}) {
  return (
    <div className="field">
      <div className="label-row"><label htmlFor={id}>{label}</label>{forgot}</div>
      <input id={id} type="password" value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete}
        minLength={minLength} required />
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ReactNode>(null);
  const run = (fn: () => Promise<void>) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

export function SignUpPage() {
  const site = useSite();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { busy, error, run } = useSubmit();
  const submit = run(async () => {
    const { error: err } = await authClient.signUp.email({ name, email, password, callbackURL: VERIFIED_CALLBACK });
    if (err) throw new Error(err.message || t('somethingWrong'));
    navigate(`/check-email?email=${encodeURIComponent(email)}`);
  });
  return (
    <AuthCard title={t('signUp')} intro={t('signUpIntro')}>
      {site?.signUpsOpen === false ? <p className="banner" role="status">{t('signUpsClosed')}</p> : (
      <form onSubmit={submit} className="stack">
        <label>{t('name')}<input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required /></label>
        <label>{t('email')}<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>
        <label>{t('password')}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} required />
          <span className="hint">{t('passwordHint')}</span>
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        <button className="primary" disabled={busy}>{t('signUp')}</button>
      </form>
      )}
      <p className="muted small link-row">{t('haveAccount')} <Link href="/signin" className="button small">{t('signIn')}</Link></p>
    </AuthCard>
  );
}

export function CheckEmailPage() {
  const email = useLocation().searchParams.get('email') || '';
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit();
  const resend = run(async () => {
    const { error: err } = await authClient.sendVerificationEmail({ email, callbackURL: VERIFIED_CALLBACK });
    if (err) throw new Error(err.message || t('somethingWrong'));
    setSent(true);
  });
  return (
    <AuthCard title={t('checkEmailTitle')}>
      <p>{t('checkEmailBody', { email })}</p>
      <form onSubmit={resend} className="stack">
        {error && <p className="error" role="alert">{error}</p>}
        {sent ? <p className="ok">{t('resent')}</p> : <button disabled={busy || !email}>{t('resend')}</button>}
      </form>
      <DevMailboxHint />
      <p className="muted small"><Link href="/signin" className="button small back">← {t('backToSignIn')}</Link></p>
    </AuthCard>
  );
}

export function SignInPage() {
  const loc = useLocation();
  const { data: session, refetch } = useSession();
  const next = safeNext(loc.searchParams.get('next'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [unverified, setUnverified] = useState(false);
  const { busy, error, setError, run } = useSubmit();
  // Already signed in - in another tab, or the session arrived a moment late: carry on.
  useEffect(() => { if (session) navigate(next, { replace: true }); }, [session, next]);
  const submit = run(async () => {
    setUnverified(false);
    // no callbackURL here: given one, Better Auth's client goes there after EVERY sign-in, not only
    // from the confirmation link it sends an address not yet confirmed (that link comes back to "/")
    const { error: err } = await authClient.signIn.email({ email, password });
    if (err) {
      if (err.code === 'EMAIL_NOT_VERIFIED') {
        setUnverified(true);
        setError(t('notVerified'));
        return;
      }
      if (err.status === 401) {
        setError(<>{t('wrongPassword')} <Link href="/forgot" className="button small" onClick={() => rememberEmail(email)}>{t('resetByEmail')}</Link></>);
        return;
      }
      // too many tries from here, or this account locked after wrong passwords (apps/api/src/guard.ts)
      if (err.status === 429) {
        rememberEmail(email);
        setError(<>{err.message || t('somethingWrong')} <ContactButton subject={t('contactSubjectSignIn')} /></>);
        return;
      }
      throw new Error(err.message || t('somethingWrong'));
    }
    // The page must know about the new session before it moves on: going first
    // made it look signed out, and sent people back here to sign in a second time.
    markActive();
    await refetch();
    navigate(next, { replace: true });
  });
  return (
    <AuthCard title={t('signIn')}>
      {loc.searchParams.get('reset') && <p className="ok">{t('resetDone')}</p>}
      {loc.searchParams.get('idle') && <p className="ok">{t('idleSignedOut')}</p>}
      <form onSubmit={submit} className="stack">
        <label>{t('email')}<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>
        <PasswordField id="signin-password" label={t('password')} value={password} onChange={setPassword} autoComplete="current-password"
          forgot={<Link href="/forgot" className="button small" onClick={() => rememberEmail(email)}>{t('forgotPassword')}</Link>} />
        {error && <p className="error" role="alert">{error}</p>}
        {unverified && (
          <button type="button" onClick={() => navigate(`/check-email?email=${encodeURIComponent(email)}`)}>{t('resend')}</button>
        )}
        <button className="primary" disabled={busy}>{t('signIn')}</button>
      </form>
      <p className="muted small link-row">{t('noAccount')} <Link href="/signup" className="button small">{t('signUp')}</Link></p>
      <HelpLine />
      {/* the same page on a phone: a code its camera opens (site-code.tsx) */}
      <SiteCode />
    </AuthCard>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState(recallEmail);
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit();
  const submit = run(async () => {
    const { error: err } = await authClient.requestPasswordReset({ email, redirectTo: '/reset-password' });
    if (err) throw new Error(err.message || t('somethingWrong'));
    setSent(true);
  });
  return (
    <AuthCard title={t('forgotTitle')} intro={sent ? undefined : t('forgotBody')}>
      {sent ? <><p className="ok">{t('forgotSent', { email })}</p><DevMailboxHint /></> : (
        <form onSubmit={submit} className="stack">
          <label>{t('email')}<input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required /></label>
          {error && <p className="error" role="alert">{error}</p>}
          <button className="primary" disabled={busy}>{t('sendLink')}</button>
        </form>
      )}
      <HelpLine />
      <p className="muted small"><Link href="/signin" className="button small back">← {t('backToSignIn')}</Link></p>
    </AuthCard>
  );
}

export function ResetPage() {
  const q = useLocation().searchParams;
  const token = q.get('token');
  const [password, setPassword] = useState('');
  const { busy, error, run } = useSubmit();
  const submit = run(async () => {
    const { error: err } = await authClient.resetPassword({ newPassword: password, token: token! });
    if (err) throw new Error(err.code === 'INVALID_TOKEN' ? t('resetBadLink') : err.message || t('somethingWrong'));
    // a reset signs out every session, this tab's too: start afresh so nothing still thinks it is signed in
    location.replace('/signin?reset=1');
  });
  if (!token || q.get('error')) {
    return (
      <AuthCard title={t('resetTitle')}>
        <p className="error">{t('resetBadLink')}</p>
        <p className="muted small"><Link href="/forgot" className="button small">{t('forgotPassword')}</Link></p>
      </AuthCard>
    );
  }
  return (
    <AuthCard title={t('resetTitle')}>
      <form onSubmit={submit} className="stack">
        <label>{t('newPassword')}
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={8} required />
          <span className="hint">{t('passwordHint')}</span>
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        <button className="primary" disabled={busy}>{t('resetTitle')}</button>
      </form>
    </AuthCard>
  );
}

/**
 * The person's own account: their name, their email address (shown, not
 * changeable - apps/api/src/auth.ts), their password, the devices they are
 * signed in on, appearance, how pictures move on this device, and deleting it all.
 */
export function AccountPage() {
  const { data: session } = useSession();
  const site = useSite();
  if (!session) return <main className="page narrow"><p className="muted">{t('loading')}</p></main>;
  const user = session.user;
  return (
    <main className="page narrow account">
      <p className="row spread"><Link href="/" className="button back">← {t('myCards')}</Link><TipButton /></p>
      <div className="row spread">
        <h1>{t('account')}</h1>
        <button onClick={() => signOutAndLeave()}>{t('signOut')}</button>
      </div>
      <NameSection name={user.name} />
      <EmailSection email={user.email} />
      <PasswordSection email={user.email} />
      <DevicesSection current={session.session.id} idleMinutes={site?.idleMinutes} />
      <section className="settings appearance">
        <h2>{t('theme')}</h2>
        <ThemeSwitch />
        <p className="muted small">{t('themeHint')}</p>
      </section>
      <PositionLockSetting />
      <DeleteSection email={user.email} />
    </main>
  );
}

function NameSection({ name: saved }: { name: string }) {
  const [name, setName] = useState(saved);
  const [done, setDone] = useState(false);
  const { busy, error, run } = useSubmit();
  const submit = run(async () => {
    setDone(false);
    const { error: err } = await authClient.updateUser({ name: name.trim() });
    if (err) throw new Error(err.message || t('somethingWrong'));
    setDone(true);
  });
  return (
    <section className="settings">
      <h2>{t('profile')}</h2>
      <form onSubmit={submit} className="stack">
        <label>{t('name')}<input value={name} onChange={(e) => { setName(e.target.value); setDone(false); }} autoComplete="name" required /></label>
        {error && <p className="error" role="alert">{error}</p>}
        {done && <p className="ok" role="status">{t('nameSaved')}</p>}
        <button className="primary" disabled={busy || !name.trim() || name.trim() === saved}>{t('saveName')}</button>
      </form>
    </section>
  );
}

/** The sign-in address, shown and not changeable (2026-10-10; see apps/api/src/auth.ts). */
function EmailSection({ email }: { email: string }) {
  return (
    <section className="settings">
      <h2>{t('emailAddress')}</h2>
      <p><strong>{email}</strong></p>
      <p className="muted small">{t('emailFixed')}</p>
    </section>
  );
}

function PasswordSection({ email }: { email: string }) {
  const [current, setCurrent] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  /** Forgot the current one: the reset link goes straight to the account's own address. */
  const sendReset = async () => {
    setResetBusy(true);
    const { error: err } = await authClient.requestPasswordReset({ email, redirectTo: '/reset-password' });
    setResetBusy(false);
    setResetSent(!err);
  };
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [others, setOthers] = useState(true);
  const [done, setDone] = useState(false);
  const { busy, error, run } = useSubmit();
  const submit = run(async () => {
    setDone(false);
    if (next !== again) throw new Error(t('passwordsDiffer'));
    const { error: err } = await authClient.changePassword({ currentPassword: current, newPassword: next, revokeOtherSessions: others });
    if (err) throw new Error(err.code === 'INVALID_PASSWORD' ? t('wrongCurrentPassword') : err.message || t('somethingWrong'));
    setCurrent(''); setNext(''); setAgain('');
    setDone(true);
  });
  return (
    <section className="settings">
      <h2>{t('changePassword')}</h2>
      <form onSubmit={submit} className="stack">
        <PasswordField id="current-password" label={t('currentPassword')} value={current} onChange={setCurrent} autoComplete="current-password"
          forgot={<button type="button" className="link small" disabled={resetBusy} onClick={sendReset}>{t('forgotPassword')}</button>} />
        {resetSent && <p className="ok" role="status">{t('resetLinkSent', { email })}</p>}
        <label>{t('newPassword')}
          <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" minLength={8} required />
          <span className="hint">{t('passwordHint')}</span>
        </label>
        <label>{t('confirmPassword')}
          <input type="password" value={again} onChange={(e) => setAgain(e.target.value)} autoComplete="new-password" minLength={8} required />
        </label>
        <label className="check"><input type="checkbox" checked={others} onChange={(e) => setOthers(e.target.checked)} />{t('signOutOthers')}</label>
        {error && <p className="error" role="alert">{error}</p>}
        {done && <p className="ok" role="status">{t('passwordChanged')}</p>}
        <button className="primary" disabled={busy}>{t('changePassword')}</button>
      </form>
    </section>
  );
}

interface Device { id: string; token: string; userAgent?: string | null; ipAddress?: string | null; createdAt: Date | string; updatedAt: Date | string }

function DevicesSection({ current, idleMinutes }: { current: string; idleMinutes?: number }) {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => authClient.listSessions().then(({ data }) => setDevices((data as Device[] | null) ?? []));
  useEffect(() => { void load(); }, [current]);
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); await load(); } finally { setBusy(false); }
  };
  const sorted = [...(devices || [])].sort((a, b) => (a.id === current ? -1 : b.id === current ? 1
    : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
  const others = sorted.filter((d) => d.id !== current);
  return (
    <section className="settings">
      <h2>{t('devices')}</h2>
      {!devices ? <p className="muted">{t('loading')}</p> : (
        <ul className="device-list">
          {sorted.map((d) => (
            <li key={d.id}>
              <div>
                <strong>{deviceName(d.userAgent)}</strong>{d.id === current && <span className="status status-approved">{t('thisDevice')}</span>}
                <div className="muted small">{t('signedInSince', { when: ago(String(d.createdAt)) })} · {t('lastUsed', { when: ago(String(d.updatedAt)) })}</div>
              </div>
              {d.id !== current && (
                <button disabled={busy} onClick={() => act(() => authClient.revokeSession({ token: d.token }))}>{t('signOutDevice')}</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {devices && (others.length
        ? <button disabled={busy} onClick={() => act(() => authClient.revokeOtherSessions())}>{t('signOutOtherDevices')}</button>
        : <p className="muted small">{t('noOtherDevices')}</p>)}
      {idleMinutes && <p className="muted small">{t('idleHint', { minutes: idleMinutes })}</p>}
    </section>
  );
}

function DeleteSection({ email }: { email: string }) {
  const [confirm, setConfirm] = useState('');
  const { busy, error, run } = useSubmit();
  const submit = run(async () => {
    const me = await api.me();
    await api.deleteAccount(confirm);
    // and its cards and pictures on this device (other devices keep theirs until their browser data is cleared)
    await deleteAccountDb(me.id);
    await signOutAndLeave('/signup');
  });
  return (
    <section className="danger">
      <h2>{t('deleteAccount')}</h2>
      <p className="muted">{t('deleteAccountBody')}</p>
      <form onSubmit={submit} className="stack">
        <input type="email" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={email} aria-label={t('email')} />
        {error && <p className="error" role="alert">{error}</p>}
        <button className="danger" disabled={busy || confirm.trim().toLowerCase() !== email.toLowerCase()}>{t('deleteAccountButton')}</button>
      </form>
    </section>
  );
}
